import React, { useState } from "react";
import { LogOut, UserCog, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";

const initials = (name = "", email = "") => {
  const base = (name || email || "?").trim();
  const parts = base.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
};

export const ProfileMenu = () => {
  const { t } = useApp();
  const { user, logout, updateProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null;

  const openDialog = () => {
    setName(user.name || "");
    setPassword("");
    setOpen(true);
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateProfile({ name: name.trim(), password: password || undefined });
      toast.success(t("profileUpdated"));
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("authError"));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await logout();
    toast.success(t("loggedOut"));
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="profile-menu-btn"
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-sm hover:brightness-110 transition-all shrink-0"
            aria-label={t("profile")}
          >
            {initials(user.name, user.email)}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" data-testid="profile-menu">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span className="font-semibold truncate" data-testid="profile-name">{user.name}</span>
              <span className="text-xs font-normal text-muted-foreground truncate">{user.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="profile-edit-item" onClick={openDialog}>
            <UserCog className="w-4 h-4 mr-2" /> {t("profile")}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="logout-item" onClick={doLogout} className="text-destructive focus:text-destructive">
            <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("profileTitle")}</DialogTitle>
            <DialogDescription className="sr-only">{t("profileTitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t("name")}</Label>
              <input
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-11 bg-background border border-border rounded-xl px-3 outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("newPassword")}</Label>
              <input
                data-testid="profile-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("newPasswordPlaceholder")}
                className="w-full h-11 bg-background border border-border rounded-xl px-3 outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
              />
            </div>
            <div className="rounded-xl bg-secondary px-3 py-2 text-sm text-muted-foreground">{user.email}</div>
          </div>
          <DialogFooter>
            <button
              data-testid="profile-cancel-btn"
              onClick={() => setOpen(false)}
              className="h-10 px-4 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              data-testid="profile-save-btn"
              onClick={save}
              disabled={busy}
              className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold flex items-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {t("save")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
